import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import {
  useUpdateAccessCode,
  useDeleteAccessCode,
  useCreateAccessCode,
} from "../../api/mutations";
import { useGetAccessCodes } from "../../api/queries";
import { AccessCodeI } from "../../types";
import { getRbacErrorMessage } from "../../constants/rbacMessages";
import { useNotification } from "../../hooks/useNotification";
import Button from "../../componentLibrary/Button";
import ConfirmationBox from "../../componentLibrary/ConfirmationBox";
import Spinner from "../../componentLibrary/Spinner";
import AccessCodeCard from "./AccessCodeCard";
import AccessCodeModal from "./AccessCodeModal";
import AccessCodeDetailsModal from "./AccessCodeDetailsModal";
import CreateAccessCodeModal from "./CreateAccessCodeModal";

export default function AccessCodePage() {
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCode, setSelectedCode] = useState<AccessCodeI | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Fetch access codes with search query
  const {
    data: accessCodes,
    isLoading,
    error,
  } = useGetAccessCodes(
    undefined,
    undefined,
    undefined,
    searchQuery.trim() || undefined,
  );

  // Mutations
  const createMutation = useCreateAccessCode({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["access-codes"] });
      setShowCreateModal(false);
    },
    onError: (error) => {
      notify("error", getRbacErrorMessage(error));
    },
  });

  const updateMutation = useUpdateAccessCode({
    onSuccess: async (updatedAccessCode) => {
      await queryClient.invalidateQueries({ queryKey: ["access-codes"] });
      await queryClient.invalidateQueries({
        queryKey: ["access-code-analytics"],
      });
      setSelectedCode(updatedAccessCode); // Update selectedCode with fresh data
      setShowEditModal(false);
    },
    onError: (error) => {
      notify("error", getRbacErrorMessage(error));
    },
  });

  const deleteMutation = useDeleteAccessCode({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["access-codes"] });
      await queryClient.invalidateQueries({
        queryKey: ["access-code-analytics"],
      });
      setSelectedCode(null);
      setShowDeleteConfirm(false);
    },
    onError: (error) => {
      notify("error", getRbacErrorMessage(error));
    },
  });

  const handleCardClick = (code: AccessCodeI) => {
    setSelectedCode(code);
  };

  const handleCreateCode = (data: {
    code: string;
    limitCount?: number;
    segment: string;
    expirationDate?: Date;
  }) => {
    createMutation.mutate(data);
  };

  const handleModalSave = (updates: Partial<AccessCodeI>) => {
    if (!selectedCode?._id) return;
    updateMutation.mutate({ id: selectedCode._id, body: updates });
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleDeleteClick = () => {
    // Show delete confirmation
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedCode?._id) return;
    deleteMutation.mutate(selectedCode._id);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner className="w-16 h-16" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-2">
            Error Loading Access Codes
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Access Code Management
          </h1>
          <p className="text-gray-600">
            Manage and monitor all access codes for your application
          </p>
        </div>

        <div className="mb-6 flex flex-col md:flex-row gap-4 items-stretch md:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by access code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-12 pl-10 pr-4 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-electrum focus:border-electrum"
            />
          </div>
          <Button
            variant="dark"
            className="flex items-center gap-2 whitespace-nowrap"
            onClick={() => setShowCreateModal(true)}
          >
            <PlusIcon className="w-5 h-5" />
            Create New Code
          </Button>
        </div>

        {!accessCodes || accessCodes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-center">
              <div className="text-6xl mb-4">🔑</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {searchQuery
                  ? "No access codes found"
                  : "No access codes available"}
              </h3>
              <p className="text-gray-500">
                {searchQuery
                  ? "Try adjusting your search query"
                  : "Access codes will appear here once created"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {accessCodes.map((code) => (
              <AccessCodeCard
                key={code._id}
                accessCode={code}
                onClick={() => handleCardClick(code)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedCode && !showDeleteConfirm && !showEditModal && (
        <AccessCodeDetailsModal
          accessCode={selectedCode}
          isOpen={!!selectedCode && !showDeleteConfirm && !showEditModal}
          onClose={() => setSelectedCode(null)}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
        />
      )}

      {selectedCode && showEditModal && (
        <AccessCodeModal
          accessCode={selectedCode}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleModalSave}
          onDelete={handleDeleteClick}
        />
      )}

      <CreateAccessCodeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateCode}
      />

      <ConfirmationBox
        isOpen={showDeleteConfirm}
        title="Delete Access Code"
        message={
          <div>
            <p className="mb-2">
              Are you sure you want to delete the access code{" "}
              <strong>{selectedCode?.accessCode}</strong>?
            </p>
            <p className="text-sm text-gray-600">
              This action cannot be undone.
            </p>
          </div>
        }
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
}
